import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api from '@/lib/api';
import { ArrowLeft, Save, X, ImagePlus, Info, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';


export default function EditListingPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, tc } = useTranslation('editListing');
  const { t: tCond } = useTranslation('conditions');
  const { t: tSector } = useTranslation('sectors');
  const { t: tCat } = useTranslation('categories');

  const [form, setForm] = useState(null);
  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [removedImages, setRemovedImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [sectorsData, setSectorsData] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteDetail, setDeleteDetail] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    api.get('/sectors').then(r => setSectorsData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get(`/listings/${id}`)
      .then(r => {
        const d = r.data;
        setForm({
          title: d.title || '', description: d.description || '', price: d.price || '',
          quantity: d.quantity || 1, condition: d.condition || '',
          sector: d.sector || '', category: d.category || '',
          brand: d.brand || '', reference: d.reference || '', location: d.location || '',
          delivery: d.delivery || false, pickup: d.pickup !== false,
          invoice: d.invoice || false, warranty: d.warranty || false,
        });
        setExistingImages(d.images || []);
      })
      .catch(() => setForm(null))
      .finally(() => setFetching(false));
  }, [id]);

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const sectorKeys = Object.keys(sectorsData);
  const categories = form && form.sector && sectorsData[form.sector] ? sectorsData[form.sector].categories : [];

  const handleImageAdd = (e) => {
    const files = Array.from(e.target.files);
    const totalCount = existingImages.length - removedImages.length + newImages.length + files.length;
    if (totalCount > 6) { toast.error('Maximum 6 photos'); return; }
    const ni = files.map(f => ({ file: f, preview: URL.createObjectURL(f) }));
    setNewImages(prev => [...prev, ...ni]);
  };

  const removeExistingImage = (img) => setRemovedImages(prev => [...prev, img]);
  const removeNewImage = (idx) => setNewImages(prev => prev.filter((_, i) => i !== idx));

  // Build combined ordered list for display and reordering
  const activeExistingImages = existingImages.filter(url => !removedImages.includes(url));
  const allImages = [
    ...activeExistingImages.map((url, i) => ({ type: 'existing', url, idx: i })),
    ...newImages.map((img, i) => ({ type: 'new', preview: img.preview, idx: i })),
  ];

  const moveImageInOrder = (fromPos, toPos) => {
    if (toPos < 0 || toPos >= allImages.length) return;
    const item = allImages[fromPos];
    const target = allImages[toPos];
    // Swap positions by rebuilding both arrays
    const newExisting = [...activeExistingImages];
    const newNew = [...newImages];
    if (item.type === 'existing' && target.type === 'existing') {
      const tmp = newExisting[item.idx];
      newExisting[item.idx] = newExisting[target.idx];
      newExisting[target.idx] = tmp;
      setExistingImages(newExisting);
      setRemovedImages([]);
    } else if (item.type === 'new' && target.type === 'new') {
      const tmp = newNew[item.idx];
      newNew[item.idx] = newNew[target.idx];
      newNew[target.idx] = tmp;
      setNewImages(newNew);
    } else {
      // Cross-type: rebuild both lists maintaining order
      const combined = allImages.map(a => a);
      const [moved] = combined.splice(fromPos, 1);
      combined.splice(toPos, 0, moved);
      const reExisting = combined.filter(a => a.type === 'existing').map(a => a.url);
      const reNew = combined.filter(a => a.type === 'new').map(a => newImages[a.idx]);
      setExistingImages(reExisting);
      setRemovedImages([]);
      setNewImages(reNew);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.price) {
      toast.error(t('fillRequired'));
      return;
    }
    setLoading(true);
    try {
      // 1. Upload new images
      let newImageUrls = [];
      if (newImages.length > 0) {
        const formData = new FormData();
        newImages.forEach(img => formData.append('files', img.file));
        const uploadRes = await api.post('/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        newImageUrls = uploadRes.data.urls || [];
      }
      // 2. Combine existing (minus removed) + new
      const finalImages = [...activeExistingImages, ...newImageUrls];
      // 3. Update listing with JSON
      await api.put(`/listings/${id}`, {
        ...form,
        price: parseFloat(form.price),
        images: finalImages,
      });
      toast.success(t('saveSuccess'));
      navigate(`/annonces/${id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('saveError'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteReason) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/listings/${id}?reason=${deleteReason}&reason_detail=${encodeURIComponent(deleteDetail)}`);
      toast.success('Annonce supprimée');
      navigate('/tableau-de-bord');
    } catch {
      toast.error('Erreur lors de la suppression');
    } finally { setDeleteLoading(false); }
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F3F72]" />
      </div>
    );
  }

  if (!form) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center" data-testid="edit-listing-not-found">
        <p className="text-lg font-semibold text-neutral-700">{t('notFound')}</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/tableau-de-bord')}>
          {t('backToDashboard')}
        </Button>
      </div>
    );
  }

  const totalImages = activeExistingImages.length + newImages.length;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="edit-listing-page">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/tableau-de-bord')} data-testid="back-to-dashboard-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <h1 className="font-heading text-2xl font-bold text-neutral-900">{t('title')}</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* General */}
        <section className="bg-white border rounded-xl p-6 space-y-5" data-testid="edit-general-section">
          <h2 className="font-heading text-lg font-semibold text-neutral-900">{t('generalInfo')}</h2>
          <div className="space-y-2">
            <Label>{t('adTitle')} *</Label>
            <Input value={form.title} onChange={e => update('title', e.target.value)} data-testid="edit-title-input" />
            <p className="text-xs text-neutral-500 leading-relaxed" data-testid="edit-title-help">{t('adTitleHelp')}</p>
          </div>
          <div className="space-y-2">
            <Label>{t('description')} *</Label>
            <Textarea rows={4} placeholder={t('descPlaceholder')} value={form.description} onChange={e => update('description', e.target.value)} data-testid="edit-description-input" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
            <div className="space-y-2">
              <Label>{t('price')} *</Label>
              <Input type="number" step="0.01" min="0" value={form.price} onChange={e => update('price', e.target.value)} data-testid="edit-price-input" />
            </div>
            <div className="space-y-2">
              <Label>{t('quantity')}</Label>
              <Input type="number" min="1" value={form.quantity} onChange={e => update('quantity', parseInt(e.target.value) || 1)} data-testid="edit-quantity-input" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label>{t('condition')}</Label>
                <div className="relative group">
                  <Info className="w-4 h-4 text-neutral-400 cursor-help" />
                  <div className="absolute top-full left-0 mt-2 w-72 p-3 bg-neutral-900 text-white text-xs rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 sm:left-1/2 sm:-translate-x-1/2">
                    <p className="font-semibold mb-1.5">{t('conditionTooltipExamples')}</p>
                    <p className="mb-1">{t('conditionEx1')}</p>
                    <p className="mb-1">{t('conditionEx2')}</p>
                    <p className="mb-1">{t('conditionEx3')}</p>
                    <p className="mb-1">{t('conditionEx4')}</p>
                    <p className="mb-1">{t('conditionEx5')}</p>
                    <p>{t('conditionEx6')}</p>
                  </div>
                </div>
              </div>
              <Input placeholder={t('conditionPlaceholder')} value={form.condition} onChange={e => update('condition', e.target.value)} data-testid="edit-condition-input" />
            </div>
          </div>
        </section>

        {/* Classification */}
        <section className="bg-white border rounded-xl p-6 space-y-5" data-testid="edit-classification-section">
          <h2 className="font-heading text-lg font-semibold text-neutral-900">{t('classification')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('sector')}</Label>
              <select className="w-full rounded-md border border-neutral-200 px-3 py-2 text-sm bg-white" value={form.sector} onChange={e => { update('sector', e.target.value); update('category', ''); }} data-testid="edit-sector-select">
                <option value="">{t('sectorPlaceholder')}</option>
                {sectorKeys.map(s => <option key={s} value={s}>{tSector(s)}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label>{t('categoryLabel')} *</Label>
              <select className="w-full rounded-md border border-neutral-200 px-3 py-2 text-sm bg-white" value={form.category} onChange={e => update('category', e.target.value)} disabled={!form.sector} data-testid="edit-category-select">
                <option value="">{t('categoryPlaceholder')}</option>
                {categories.map(c => <option key={c} value={c}>{tCat(c)}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label>{t('brand')}</Label>
              <Input value={form.brand} onChange={e => update('brand', e.target.value)} data-testid="edit-brand-input" />
            </div>
            <div className="space-y-2">
              <Label>{t('reference')}</Label>
              <Input value={form.reference} onChange={e => update('reference', e.target.value)} data-testid="edit-reference-input" />
            </div>
          </div>
        </section>

        {/* Photos */}
        <section className="bg-white border rounded-xl p-6 space-y-5" data-testid="edit-photos-section">
          <div>
            <h2 className="font-heading text-lg font-semibold text-neutral-900">{t('photos')}</h2>
            <p className="text-xs text-neutral-500 mt-0.5">{t('photosSubtitle')}</p>
          </div>
          {allImages.length > 1 && (
            <p className="text-xs text-neutral-400">Glissez les photos pour changer l'ordre. La premiere photo sera la photo principale.</p>
          )}
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
            {allImages.map((item, pos) => {
              const src = item.type === 'existing'
                ? (item.url.startsWith('http') ? item.url : `${process.env.REACT_APP_BACKEND_URL}${item.url}`)
                : item.preview;
              const onRemove = item.type === 'existing'
                ? () => removeExistingImage(item.url)
                : () => removeNewImage(item.idx);
              return (
                <div key={`${item.type}-${item.idx}`} className={`relative aspect-square rounded-lg overflow-hidden border-2 cursor-grab active:cursor-grabbing transition-all ${pos === 0 ? 'border-[#F25C05] ring-2 ring-[#F25C05]/20' : 'border-neutral-200'}`}>
                  <img src={src} alt="" className="w-full h-full object-cover" />
                  {pos === 0 && (
                    <span className="absolute top-1 left-1 bg-[#F25C05] text-white text-[9px] font-bold px-1.5 py-0.5 rounded">Principale</span>
                  )}
                  {pos > 0 && (
                    <span className="absolute top-1 left-1 bg-black/50 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">{pos + 1}</span>
                  )}
                  <button type="button" onClick={onRemove} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5">
                    <X className="w-3 h-3" />
                  </button>
                  {allImages.length > 1 && (
                    <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-1">
                      {pos > 0 && (
                        <button type="button" onClick={() => moveImageInOrder(pos, pos - 1)} className="bg-black/60 hover:bg-black/80 text-white rounded-full p-0.5 transition-colors">
                          <ChevronLeft className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {pos < allImages.length - 1 && (
                        <button type="button" onClick={() => moveImageInOrder(pos, pos + 1)} className="bg-black/60 hover:bg-black/80 text-white rounded-full p-0.5 transition-colors">
                          <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {totalImages < 6 && (
              <label className="aspect-square rounded-lg border-2 border-dashed border-neutral-300 flex flex-col items-center justify-center cursor-pointer hover:border-[#1F3F72] hover:bg-neutral-50 transition-colors">
                <ImagePlus className="w-6 h-6 text-neutral-400" />
                <input type="file" accept="image/*" multiple className="hidden" onChange={handleImageAdd} />
              </label>
            )}
          </div>
        </section>

        {/* Location & Options */}
        <section className="bg-white border rounded-xl p-6 space-y-5" data-testid="edit-options-section">
          <h2 className="font-heading text-lg font-semibold text-neutral-900">{t('locationOptions')}</h2>
          <div className="space-y-2">
            <Label>{t('location')}</Label>
            <Input placeholder={t('locationPlaceholder')} value={form.location} onChange={e => update('location', e.target.value)} data-testid="edit-location-input" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { key: 'delivery', label: t('deliveryAvailable') },
              { key: 'pickup', label: t('pickupOnSite') },
              { key: 'invoice', label: t('invoiceAvailable') },
              { key: 'warranty', label: t('warrantyLabel') },
            ].map(opt => (
              <label key={opt.key} className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer text-xs ${form[opt.key] ? 'border-[#1F3F72] bg-blue-50 text-[#1F3F72]' : 'border-neutral-200 text-neutral-600'}`}>
                <input type="checkbox" checked={form[opt.key]} onChange={e => update(opt.key, e.target.checked)} className="rounded" />
                {opt.label}
              </label>
            ))}
          </div>
        </section>

        {/* Submit */}
        <Button type="submit" className="w-full bg-[#1F3F72] hover:bg-[#19325B] text-white h-12 text-base" disabled={loading} data-testid="edit-submit-btn">
          {loading ? t('saving') : <><Save className="w-4 h-4 mr-2" /> {t('saveBtn')}</>}
        </Button>

        {/* Delete */}
        <Button
          type="button"
          variant="outline"
          className="w-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 h-11"
          onClick={() => setShowDeleteModal(true)}
          data-testid="edit-delete-btn"
        >
          <Trash2 className="w-4 h-4 mr-2" /> {t('deleteListing')}
        </Button>
      </form>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50" data-testid="delete-listing-modal" onClick={() => { setShowDeleteModal(false); setDeleteReason(''); setDeleteDetail(''); }}>
          <div className="bg-white dark:bg-neutral-800 rounded-t-2xl p-6 pb-8 w-full max-w-3xl space-y-4 shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-bold text-neutral-900 dark:text-white">{t('deleteTitle')}</h3>

            <div className="space-y-2">
              {[
                { value: 'sold_destockpro', label: t('deleteReasonSold'), icon: '', color: 'border-emerald-300 bg-emerald-50 text-emerald-800' },
                { value: 'sold_elsewhere', label: t('deleteReasonElsewhere'), icon: '', color: 'border-blue-200 bg-blue-50 text-blue-800' },
                { value: 'not_available', label: t('deleteReasonUnavailable'), icon: '', color: 'border-neutral-200 bg-neutral-50 text-neutral-700' },
                { value: 'other', label: t('deleteReasonOther'), icon: '', color: 'border-neutral-200 bg-neutral-50 text-neutral-700' },
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setDeleteReason(opt.value)}
                  className={`w-full text-left px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all ${
                    deleteReason === opt.value
                      ? `${opt.color} ring-2 ring-offset-1 ${opt.value === 'sold_destockpro' ? 'ring-emerald-400' : 'ring-[#1F3F72]/30'}`
                      : 'border-neutral-200 hover:border-neutral-300 text-neutral-600'
                  }`}
                  data-testid={`delete-reason-${opt.value}`}
                >
                  {opt.icon && <span className="mr-2">{opt.icon}</span>}
                  {opt.label}
                </button>
              ))}
            </div>

            {deleteReason === 'other' && (
              <Textarea
                placeholder={t('deleteReasonDetailPlaceholder')}
                value={deleteDetail}
                onChange={(e) => setDeleteDetail(e.target.value)}
                rows={2}
                className="text-sm"
                data-testid="delete-reason-detail"
              />
            )}

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => { setShowDeleteModal(false); setDeleteReason(''); setDeleteDetail(''); }}
                data-testid="delete-listing-cancel"
              >
                {t('deleteCancel')}
              </Button>
              <Button
                className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                onClick={handleDelete}
                disabled={!deleteReason || deleteLoading}
                data-testid="delete-listing-confirm"
              >
                {deleteLoading ? t('deleteDeleting') : t('deleteConfirmBtn')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
